import { getVideoTranslations } from 'src/core/video/videoTranslations';
import I18nError from 'utils/I18nError';

/** HTTP statuses that indicate a per-camera access failure (not a session problem). */
const AUTH_FAILURE_STATUSES = new Set([401, 403]);

/**
 * A camera video request returned an auth failure (401/403). This is scoped to
 * the individual camera stream (commonly a relay stream-token rejection caused
 * by client/relay clock drift) — NOT the app session. It is surfaced as a
 * per-tile "camera not accessible" message and must never trigger a global
 * logout. See SL-15542.
 */
export class CameraNotAccessibleError extends I18nError {
  readonly code = 'CAMERA_NOT_ACCESSIBLE';
  /** The originating HTTP status (401/403), preserved so custom loaders can
   *  propagate it to hls.js instead of flattening it to 0. */
  readonly status: number | undefined;

  constructor(status?: number) {
    super(
      `Camera stream not accessible (status ${status ?? 'unknown'})`,
      (locale) => getVideoTranslations(locale).cameraNotAccessible,
    );
    this.status = status;
    // Preserve instanceof across transpilation targets.
    Object.setPrototypeOf(this, CameraNotAccessibleError.prototype);
  }
}

/** True when an HTTP status is a per-camera auth failure (401/403). */
export function isAuthFailureStatus(status: number | undefined): boolean {
  return status !== undefined && AUTH_FAILURE_STATUSES.has(status);
}

/**
 * The status code a custom hls.js playlist loader should report to hls.js for a
 * caught error: the preserved auth status when it's a CameraNotAccessibleError,
 * otherwise 0 (hls.js's convention for "no status").
 */
export function authFailureCodeFor(err: unknown): number {
  return isCameraNotAccessibleError(err) ? (err.status ?? 403) : 0;
}

/**
 * Robust type guard (checks the stable `code` rather than `instanceof`, which
 * can break across lazily-loaded chunks).
 */
export function isCameraNotAccessibleError(err: unknown): err is CameraNotAccessibleError {
  return !!err && (err as { code?: string }).code === 'CAMERA_NOT_ACCESSIBLE';
}
